import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Calendar, X } from 'lucide-react'
import { useCategories } from '../../hooks/useCategories'
import CategoryService from '../../services/categoryService'

const ReportSpecificFilters = ({ reportType, filters = {}, onFilterChange, onApply, onClear, className = '' }) => {
  const [localFilters, setLocalFilters] = useState(filters)
  console.log('📋 ReportSpecificFilters: Calling useCategories hook')
  
  // Use the useCategories hook
  const { categories, loading: categoriesLoading, error: categoriesError } = useCategories()
  
  // Check if there are any active filters
  const hasActiveFilters = useMemo(() => {
    return Object.values(localFilters).some(value => 
      value !== null && 
      value !== undefined && 
      value !== '' && 
      value !== 'all'
    )
  }, [localFilters])
  
  // Also directly fetch categories for debugging
  const [directCategories, setDirectCategories] = useState([])
  
  useEffect(() => {
    const fetchDirectCategories = async () => {
      try {
        console.log('🔍 DEBUGGING: Directly fetching categories')
        const response = await CategoryService.getAllCategories()
        console.log('✅ DEBUGGING: Direct category fetch result:', response)
        if (response && response.success && response.data) {
          setDirectCategories(Array.isArray(response.data) ? response.data : [])
        }
      } catch (error) {
        console.error('❌ DEBUGGING: Direct category fetch error:', error)
      }
    }
    
    if (reportType === 'product-summary') {
      fetchDirectCategories()
    }
  }, [reportType])
  
  const debounceTimerRef = useRef(null)
  const onFilterChangeRef = useRef(onFilterChange)
  const onApplyRef = useRef(onApply)

  // Update refs when props change
  useEffect(() => {
    onFilterChangeRef.current = onFilterChange
    onApplyRef.current = onApply
  }, [onFilterChange, onApply])
  
  // Temporary mock categories for testing
  const mockCategories = useMemo(() => [
    { _id: '1', name: 'Coffee', isActive: true },
    { _id: '2', name: 'Tea', isActive: true },
    { _id: '3', name: 'Pastries', isActive: true },
    { _id: '4', name: 'Sandwiches', isActive: true }
  ], [])

  // Use mock categories if real ones are not loading
  const displayCategories = categories.length > 0 ? categories : mockCategories

  // Debug log for categories
  useEffect(() => {
    console.log('Categories debug DETAILED:', { 
      categories: categories?.length, 
      loading: categoriesLoading,
      hasError: !!categoriesError,
      errorMessage: categoriesError,
      displayCategories: displayCategories?.length,
      usingMock: displayCategories === mockCategories,
      firstCategory: categories?.[0]?.name,
      directCategoriesLength: directCategories.length,
      directCategoriesFirst: directCategories[0]?.name,
      allCategories: categories
    })
  }, [categories, categoriesLoading, categoriesError, displayCategories, mockCategories, directCategories])

  useEffect(() => {
    setLocalFilters(filters)
  }, [filters])

  // Handle filter changes with debouncing - avoid infinite loop
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    
    debounceTimerRef.current = setTimeout(() => {
      // Update parent's filter state
      if (typeof onFilterChangeRef.current === 'function') {
        onFilterChangeRef.current(localFilters)
      }
      
      // Apply the filters (reload data)  
      if (typeof onApplyRef.current === 'function') {
        onApplyRef.current(localFilters)
      }
    }, 500)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [localFilters]) // Now safe to only depend on localFilters

  const handleFilterChange = (key, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const clearFilters = () => {
    const clearedFilters = Object.keys(localFilters).reduce((acc, key) => {
      acc[key] = ''
      return acc
    }, {})
    setLocalFilters(clearedFilters)
    
    // Call parent's clear function if provided
    if (typeof onClear === 'function') {
      onClear()
    }
  }

  const formatDateForInput = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toISOString().split('T')[0]
  }

  const renderDateRangeFilter = () => (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
        <input
          type="date"
          value={formatDateForInput(localFilters.fromDate)}
          onChange={(e) => handleFilterChange('fromDate', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
        <input
          type="date"
          value={formatDateForInput(localFilters.toDate)}
          onChange={(e) => handleFilterChange('toDate', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
        />
      </div>
    </div>
  )

  if (!reportType) return null

  const renderFilterContent = () => {
    switch (reportType) {
      case 'transactions':
        return (
          <>
            <h4 className="font-medium text-gray-900 mb-3">Filter Options</h4>
            <ul className="list-disc list-inside text-sm text-gray-600 mb-4">
              <li>Date range for transaction filtering</li>
              <li>Order ID search</li>
              <li>Branch selection</li>
            </ul>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order ID</label>
                <input
                  type="text"
                  placeholder="Enter Order ID"
                  value={localFilters.orderId || ''}
                  onChange={(e) => handleFilterChange('orderId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                <select
                  value={localFilters.branch || ''}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">All Branches</option>
                  <option value="colombo">Tezlaa Colombo</option>
                  <option value="negombo">Tezlaa Negombo</option>
                  <option value="warehouse">Tezlaa Warehouse</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              {renderDateRangeFilter()}
            </div>
          </>
        )

      case 'sales-summary':
        return (
          <>
            <h4 className="font-medium text-gray-900 mb-3">Filter Options</h4>
            <ul className="list-disc list-inside text-sm text-gray-600 mb-4">
              <li>Date range for sales period</li>
              <li>Branch selection</li>
            </ul>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                <select
                  value={localFilters.branch || ''}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">All Branches</option>
                  <option value="colombo">Tezlaa Colombo</option>
                  <option value="negombo">Tezlaa Negombo</option>
                  <option value="warehouse">Tezlaa Warehouse</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              {renderDateRangeFilter()}
            </div>
          </>
        )

      case 'inventory-levels':
        return (
          <>
            <h4 className="font-medium text-gray-900 mb-3">Filter Options</h4>
            <ul className="list-disc list-inside text-sm text-gray-600 mb-4">
              <li>Branch for inventory location</li>
              <li>Search by product name</li>
              <li>Category filter</li>
            </ul>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                <select
                  value={localFilters.branch || ''}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">All Branches</option>
                  <option value="colombo">Tezlaa Colombo</option>
                  <option value="negombo">Tezlaa Negombo</option>
                  <option value="warehouse">Tezlaa Warehouse</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Search</label>
                <input
                  type="text"
                  placeholder="Product name..."
                  value={localFilters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>
          </>
        )

      case 'product-summary':
        return (
          <>
            <h4 className="font-medium text-gray-900 mb-3">Filter Options</h4>
            <ul className="list-disc list-inside text-sm text-gray-600 mb-4">
              <li>Branch</li>
              <li>Category</li>
              <li>Product</li>
            </ul>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                <select
                  value={localFilters.branch || ''}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">All Branches</option>
                  <option value="colombo">Tezlaa Colombo</option>
                  <option value="negombo">Tezlaa Negombo</option>
                  <option value="warehouse">Tezlaa Warehouse</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={localFilters.category || ''}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  disabled={categoriesLoading}
                >
                  <option value="">All Categories</option>
                  {/* Try directly fetched categories first */}
                  {directCategories && directCategories.length > 0 ? (
                    directCategories.map((category) => (
                      <option key={category._id || category.id} value={category._id || category.id}>
                        {category.name || 'Unnamed'} [Direct API]
                      </option>
                    ))
                  ) : categories && categories.length > 0 ? (
                    categories.map((category) => (
                      <option key={category._id || category.id} value={category._id || category.id}>
                        {category.name}
                      </option>
                    ))
                  ) : (
                    mockCategories.map((category) => (
                      <option key={category._id || category.id} value={category._id || category.id}>
                        {category.name} [Mock]
                      </option>
                    ))
                  )}
                </select>
                {directCategories && directCategories.length > 0 ? (
                  <p className="text-xs text-green-500 mt-1">✓ Using {directCategories.length} direct API categories</p>
                ) : categoriesLoading ? (
                  <p className="text-xs text-gray-500 mt-1">Loading categories...</p>
                ) : categories && categories.length > 0 ? (
                  <p className="text-xs text-blue-500 mt-1">✓ Using {categories.length} hook categories</p>
                ) : (
                  <p className="text-xs text-amber-500 mt-1">Using {mockCategories.length} mock categories</p>
                )}
                {categoriesError && (
                  <p className="text-xs text-red-500 mt-1">API Error: {categoriesError.toString().slice(0, 50)}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Search</label>
                <input
                  type="text"
                  placeholder="Product name..."
                  value={localFilters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>
          </>
        )

      case 'customers':
        return (
          <>
            <h4 className="font-medium text-gray-900 mb-3">Filter Options</h4>
            <ul className="list-disc list-inside text-sm text-gray-600 mb-4">
              <li>Search by ID, name, mobile number</li>
              <li>Branch</li>
            </ul>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  placeholder="Customer ID, name, or mobile..."
                  value={localFilters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                <select
                  value={localFilters.branch || ''}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">All Branches</option>
                  <option value="colombo">Tezlaa Colombo</option>
                  <option value="negombo">Tezlaa Negombo</option>
                  <option value="warehouse">Tezlaa Warehouse</option>
                </select>
              </div>
            </div>
          </>
        )

      case 'supplierReturns':
      case 'supplier-returns':
        return (
          <>
            <h4 className="font-medium text-gray-900 mb-3">Supplier Returns Filters</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderDateRangeFilter()}
            </div>
          </>
        )

      default:
        return (
          <div>
            {renderDateRangeFilter()}
          </div>
        )
    }
  }

  return (
    <div className={`bg-gray-50 p-4 rounded-lg ${className}`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-colors"
          >

            Clear Filters
          </button>
        )}
      </div>
      {renderFilterContent()}
    </div>
  )
}

export default ReportSpecificFilters